import {
    createContext,
    FormEvent,
    FormEventHandler,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

import { AdvertiserContext } from '../AdvertiserContext';
import { CampaignChangeRequest } from '../types/Campaign';
import { Dealer } from '../types/Dealer';
import { Stepper } from './useStepper';
import { UserContext } from '../App';
import {
    CampaignGeographyTargeting,
    CampaignZipCodes,
    useCampaignGeographyTargeting,
    useCampaignZipCodes,
} from '../hooks/useCampaign';
import { parseZipCodes } from './useZipCodes';
import { toCampaignAdGroupGeoLocations } from './useLocations';
import { useLoader, LoaderState } from './useLoader';
import { useInfoMessage } from './useInfoMessage';
import { CampaignDefaultSettings, useCampaignDefaultSettings } from './useCampaignDefaultSettings';
import { CampaignDuplicateValidatorState } from './useCampaign';
import { useCampaignUpdatePayload, useCampaignDuplicateValidator } from './useCampaign';
import useAudienceSources, { AudienceSources } from './useAudienceSources';
import useInventorySources, { InventorySources } from './useInventorySources';
import useLocations, { Locations } from './useLocations';
import useProductCategories, { ProductCategories } from './useProductCategories';
import useStepper from './useStepper';
import ApiService from '../ApiService';
import Utils from '../components/Utils';

const toStartOfDay = (date: Date): Date => {
    return moment.utc(moment(date).startOf('day').add(9, 'hours').format('YYYY-MM-DD HH:mm:ss')).toDate();
};

const toEndOfDay = (date: Date): Date => {
    return moment.utc(moment(date).endOf('day').add(4, 'hours').format('YYYY-MM-DD HH:mm:ss')).toDate();
};

export const MAX_FILE_SIZE = 314572800;

export const STEP_CAMPAIGN_SETUP = 0;
export const STEP_CAMPAIGN_GEOGRAPHY = 1;
export const STEP_CAMPAIGN_CREATIVE = 2;
export const STEP_CAMPAIGN_REVIEW = 3;

export class CampaignWizardContextValue {
    $audienceSources: AudienceSources | null = null;
    $inventorySources: InventorySources | null = null;
    $locations: Locations | null = null;
    $productCategories: ProductCategories | null = null;

    $loader: LoaderState | null = null;
    $campaignGeographyTargeting: CampaignGeographyTargeting | null = null;
    $campaignZipCodes: CampaignZipCodes | null = null;
    $campaignDefaultSettings: CampaignDefaultSettings | null = null;
    $campaignDuplicateValidator: CampaignDuplicateValidatorState | null = null;

    campaign: CampaignChangeRequest = new CampaignChangeRequest(undefined);
    campaignBidStrategyLabel: string = '';
    campaignBudgetManagementStrategyLabel: string = '';
    dealer: Dealer | null = null;
    deleteDraftCampaign: Function = (): Promise<any> => Promise.resolve({});
    fetchCampaign: Function = (): void => {};
    files: File[] = [];
    flightValidationState: boolean = true;
    generateCampaignName: Function = (): string => '';
    getCampaignName: Function = (): string => '';
    handleSubmit: FormEventHandler<HTMLFormElement> = (event: FormEvent<HTMLFormElement>): void => {};
    submitCampaign: Function = (): void => {};
    hasError: Function = (field: string): boolean => false;
    hasFormError: boolean = false;
    ignoreDuplicates: boolean = false;
    isCompletedCampaign: boolean = false;
    isDraftCampaign: boolean = false;
    isInvalidField: Function = (field: string): boolean => false;
    isLoaded: boolean = false;
    isNewCampaign: boolean = true;
    isValidCampaign: Function = (): boolean => false;
    isValidField: Function = (field: string): boolean => false;
    isValidStep: Function = (step: number): boolean => false;
    resetCampaignWizard: Function = (): void => {};
    saveInProgress: boolean = false;
    setCampaign: Function = (campaign: CampaignChangeRequest): void => {};
    setFiles: Function = (files: File[]): void => {};
    setFlightValidation: Function = (isValid: boolean): void => {};
    setHasFormError: Function = (hasFormError: boolean): void => {};
    setIgnoreDuplicates: Function = (ignoreDuplicates: boolean): void => {};
    setIsCompletedCampaign: Function = (ignoreDuplicates: boolean): void => {};
    setSaveInProgress: Function = (saveInProgress: boolean): void => {};
    setWasRecentlyCreated: Function = (wasRecentlyCreated: boolean): void => {};
    setWasRecentlyUpdated: Function = (wasRecentlyUpdated: boolean): void => {};
    setWasRecentlyLoaded: Function = (wasRecentlyLoaded: boolean): void => {};
    setWasRecentlySubmitted: Function = (wasRecentlySubmitted: boolean): void => {};
    stepper: Stepper = new Stepper();
    updateCampaign: Function = (): Promise<any> => Promise.resolve({});
    updateDraftCampaign: Function = (): Promise<any> => Promise.resolve({});
    validateCampaign: Function = (): boolean => false;
    wasRecentlyCreated: boolean = false;
    wasRecentlyUpdated: boolean = false;
    wasRecentlyLoaded: boolean = false;
    wasRecentlySubmitted: boolean = false;
}

export const CampaignWizardContext = createContext<CampaignWizardContextValue>(new CampaignWizardContextValue());

export function useCampaignWizardContext() {
    return useContext(CampaignWizardContext);
}

type CampaignUpdatePayload = Omit<CampaignChangeRequest, 'activationStatus'>;

export type CampaignWizardProps = {
    dealer: Dealer | null;
    campaignId?: number | null;
    campaignChangeRequestId?: number | null;
};

export default function useCampaignWizard(props: CampaignWizardProps) {
    const { campaignId, campaignChangeRequestId, dealer } = props;
    const { setDealer } = useContext(AdvertiserContext);
    const { showInfoMessage } = useInfoMessage();
    const { userContext } = useContext(UserContext);
    const navigate = useNavigate();

    const getNewCampaignInstance = (): CampaignChangeRequest => {
        let newCampaignInstance = new CampaignChangeRequest(dealer?.id, dealer);
        newCampaignInstance.startDate = moment().startOf('day').toDate();
        newCampaignInstance.endDate = moment().endOf('month').toDate();
        newCampaignInstance.campaignName = '';
        newCampaignInstance.mediaType = '';
        newCampaignInstance.strategyType = '';
        newCampaignInstance.bidStrategy = '';
        newCampaignInstance.goal = '';
        newCampaignInstance.kpi = '';
        return newCampaignInstance;
    };

    const [campaign, setCampaign] = useState<CampaignChangeRequest>(getNewCampaignInstance());
    const [files, setFiles] = useState<File[]>([]);
    const [flightValidationState, setFlightValidation] = useState<boolean>(true);
    const [hasFormError, setHasFormError] = useState<boolean>(false);
    const [ignoreDuplicates, setIgnoreDuplicates] = useState<boolean>(false);
    const [isCompletedCampaign, setIsCompletedCampaign] = useState<boolean>(false);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

    const [wasRecentlyCreated, setWasRecentlyCreated] = useState<boolean>(false);
    const [wasRecentlyUpdated, setWasRecentlyUpdated] = useState<boolean>(false);
    const [wasRecentlyLoaded, setWasRecentlyLoaded] = useState<boolean>(false);
    const [wasRecentlySubmitted, setWasRecentlySubmitted] = useState<boolean>(false);

    const $audienceSources = useAudienceSources({ dealer: dealer ?? campaign.dealer });
    const $inventorySources = useInventorySources({ dealer: dealer ?? campaign.dealer });
    const $locations = useLocations({ dealer: dealer ?? campaign.dealer });
    const $productCategories = useProductCategories({ dealer: dealer ?? campaign.dealer });

    const $loader = useLoader();
    const $campaignZipCodes = useCampaignZipCodes({ campaign });
    const $campaignGeographyTargeting = useCampaignGeographyTargeting({ campaign });
    const { fetchCampaignZipCodes } = $campaignZipCodes;
    const { fetchCampaignGeographyTargeting } = $campaignGeographyTargeting;

    const $campaignDefaultSettings = useCampaignDefaultSettings();
    const {
        fetchCampaignDefaultSettings,
        isFetchingCampaignDefaultSettings,
        campaignDefaultSettings,
        applyCampaignDefaultSettings,
        campaignDefaultSettingCriteria,
    } = $campaignDefaultSettings;

    const $campaignDuplicateValidator = useCampaignDuplicateValidator();

    const { toCampaignUpdatePayload } = useCampaignUpdatePayload();

    useEffect(() => {
        if (campaign.mediaType === 'Audio') {
            setCampaign({
                ...campaign,
                gatewayAmazonDSPIsActive: 0,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.mediaType]);

    useEffect(() => {
        if (fetchCampaignZipCodes) {
            fetchCampaignZipCodes();
        }
    }, [fetchCampaignZipCodes]);

    useEffect(() => {
        if (fetchCampaignGeographyTargeting) {
            fetchCampaignGeographyTargeting();
        }
    }, [fetchCampaignGeographyTargeting]);

    useEffect(() => {
        // Ad groups are regular working state during drafting for every toggle state —
        // the backend strips them at submission when the DSP toggle is OFF
        // (CampaignController#updateCampaign, COGPLAT-2818/2854).
        if ($locations?.locations?.length) {
            if (campaign?.adGroups?.length) {
                campaign.adGroups[0] = {
                    ...campaign.adGroups[0],
                    adGroupGeoLocations: toCampaignAdGroupGeoLocations({
                        locations: $locations.locations,
                        zipCodes: parseZipCodes(campaign.zipcodes),
                        adGroupGeoLocations: campaign.adGroups[0].adGroupGeoLocations ?? [],
                    }),
                };
            } else {
                campaign.adGroups.push({
                    adGroupGeoLocations: toCampaignAdGroupGeoLocations({
                        locations: $locations.locations,
                        zipCodes: parseZipCodes(campaign.zipcodes),
                        adGroupGeoLocations: [],
                    }),
                });
            }
        }

        setCampaign({
            ...campaign,
            adGroups: campaign.adGroups,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.zipcodes, $locations.locations]);

    const fetchCampaignDefaultSettingsCallback = useCallback(async (): Promise<void> => {
        if (fetchCampaignDefaultSettings) {
            await fetchCampaignDefaultSettings(campaignDefaultSettingCriteria);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchCampaignDefaultSettings, campaignDefaultSettingCriteria]);

    useEffect(() => {
        fetchCampaignDefaultSettingsCallback();
    }, [fetchCampaignDefaultSettingsCallback]);

    useEffect(() => {
        if ($loader) {
            if (isFetchingCampaignDefaultSettings) {
                $loader.loading('campaignDefaultSettings');
            } else {
                $loader.loaded('campaignDefaultSettings');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingCampaignDefaultSettings]);

    useEffect(() => {
        if (campaignDefaultSettings) {
            setCampaign({
                ...applyCampaignDefaultSettings(campaign, campaignDefaultSettings),
                status: null,
                statusDate: null,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignDefaultSettings]);

    useEffect(() => {
        if (!dealer) {
            resetCampaignWizard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealer]);

    const resetCampaignWizard = (): void => {
        setDealer(null);
        setCampaign(getNewCampaignInstance());
        setFiles([]);
        setFlightValidation(true);
        setHasFormError(false);
        setIgnoreDuplicates(false);
        setIsCompletedCampaign(false);
        setIsLoaded(false);
        setSaveInProgress(false);
        setWasRecentlyCreated(false);
        setWasRecentlyUpdated(false);
        setWasRecentlyLoaded(false);
        setWasRecentlySubmitted(false);

        stepper.resetStep();
    };

    const getCampaignName = (): string => {
        if (campaign.campaignName) {
            return campaign.campaignName;
        }

        return generateCampaignName(campaign);
    };

    const generateCampaignName = (_campaign: CampaignChangeRequest): string => {
        let campaignType: string = _campaign.campaignType as string;
        campaignType = campaignType ?? 'DCO';
        if (campaignType === 'DCO') {
            campaignType = 'Dynamic';
        } else if (campaignType === 'AdTag') {
            campaignType = 'Ad Tag';
        }

        return [_campaign.mediaType, campaignType, _campaign.strategyType]
            .filter((s: string | null | undefined) => s && s.length > 0)
            .join(' ');
    };

    const isNewCampaign = useMemo((): boolean => {
        return !campaign.campaignId;
    }, [campaign.campaignId]);

    const isDraftCampaign = campaign.deliveryStatusId === 'DRAFT';

    const campaignBidStrategyLabel = useMemo((): string => {
        if (campaign.bidStrategy) {
            switch (campaign.bidStrategy) {
                case 'SPEND_BUDGET_IN_FULL':
                    return 'Spend Budget in Full';

                case 'PRIORITIZE_KPI_TARGET':
                    return 'Prioritize KPI Target';
            }

            return campaign.bidStrategy;
        }

        return '';
    }, [campaign.bidStrategy]);

    const campaignBudgetManagementStrategyLabel = useMemo((): string => {
        if (campaign.automateBudgetAllocation) {
            return 'Automate Budget Allocation';
        }

        if (!campaign.automateBudgetAllocation) {
            return 'Manage Budget Manually';
        }

        return '';
    }, [campaign.automateBudgetAllocation]);

    const isInvalidField = (field: string): boolean => {
        switch (field) {
            case 'adGroups.*.adGroupAudienceSources':
                if (campaign.audienceType === 'CUSTOM') {
                    if (!campaign?.adGroups?.length) {
                        return true;
                    }

                    if (campaign.deliveryStatusId === 'DRAFT') {
                        if (!campaign.adGroups[0]?.adGroupAudienceSources?.length) {
                            return true;
                        }
                    }
                }
                break;

            case 'adGroups.*.adGroupLocations':
                if (!campaign?.adGroups?.length) {
                    return true;
                }

                if (campaign.deliveryStatusId === 'DRAFT') {
                    // Geo intent may live in zipcodes alone (e.g. audio campaigns, where the
                    // wizard converts zipcodes to ad-group geo locations on save); explicit
                    // location targets are not the only valid form of it. Only flag when
                    // neither is present (COGPLAT-2854).
                    if (!campaign.adGroups[0]?.adGroupLocations?.length && !campaign.zipcodes) {
                        return true;
                    }
                }
                break;

            case 'adGroups.*.adGroupInventorySources':
                if (!campaign?.adGroups?.length) {
                    return true;
                }

                if (campaign.deliveryStatusId === 'DRAFT') {
                    // Only require a selection when the platform has inventory defaults
                    // configured for this media type / goal. Some media types (e.g. Audio)
                    // have none, and an API-managed ad group without inventory sources is
                    // valid — it simply targets all inventory (COGPLAT-2854).
                    if (!$campaignDefaultSettings?.getCampaignInventorySources()?.length) {
                        break;
                    }

                    if (!campaign.adGroups[0]?.adGroupInventorySources?.length) {
                        return true;
                    }
                }
                break;

            case 'adGroups.*.adGroupProductCategories':
                if (!campaign?.adGroups?.length) {
                    return true;
                }

                if (!campaign.adGroups[0]?.adGroupProductCategories?.length) {
                    return true;
                }
                break;

            case 'adTagUrl':
                return campaign.campaignType === 'AdTag' && !campaign.adTagUrl;

            case 'bidStrategy':
                return !campaign.bidStrategy;

            case 'budgetCapType':
                return !campaign.budgetCapType;

            case 'budgetCapRecurrenceTimePeriod':
                return campaign.budgetCapType === 'CUSTOM' && !campaign.budgetCapRecurrenceTimePeriod;

            case 'budgetCapAmount':
                return campaign.budgetCapType === 'CUSTOM' && !campaign.budgetCapAmount;

            case 'campaignType':
                return !campaign.campaignType;

            case 'dealerId':
                return !campaign.dealerId;

            case 'endDate':
                return !campaign.endDate;

            case 'files':
                return campaign.campaignType === 'Static';

            case 'frequencyType':
                return !campaign.frequencyType;

            case 'frequencyMaxImpressions':
                return campaign.frequencyType === 'CUSTOM' && !campaign.frequencyMaxImpressions;

            case 'frequencyTimeUnitCount':
                return campaign.frequencyType === 'CUSTOM' && !campaign.frequencyTimeUnitCount;

            case 'frequencyTimeUnit':
                return campaign.frequencyType === 'CUSTOM' && !campaign.frequencyTimeUnit;

            case 'audienceType':
                return !campaign.audienceType;

            case 'goal':
                return !campaign.goal;

            case 'kpi':
                return !campaign.kpi;

            case 'mediaType':
                return campaign.mediaType === '';

            case 'startDate':
                return !campaign.startDate;

            case 'strategyType':
                return campaign.strategyType === '';

            case 'targetKpi':
                if (campaign.bidStrategy === 'PRIORITIZE_KPI_TARGET') {
                    return !['Awareness'].includes(campaign.goal as string) && !campaign.targetKpi;
                } else {
                    return !campaign.targetKpiDisabled && !campaign.targetKpi;
                }

            case 'targetKpiDisabled':
                if (campaign.bidStrategy === 'PRIORITIZE_KPI_TARGET') {
                    return false;
                } else {
                    return campaign.targetKpiDisabled === undefined;
                }

            case 'zipcodes':
                return !campaign.zipcodes;
        }

        return false;
    };

    const isValidField = (field: string): boolean => {
        return !isInvalidField(field);
    };

    const hasError = (field: string): boolean => {
        if (!hasFormError) {
            return false;
        }

        return isInvalidField(field);
    };

    const getStepFields = (step: number): string[] => {
        let fields: string[] = [];

        switch (step) {
            case STEP_CAMPAIGN_SETUP:
                fields.push(...['dealerId', 'mediaType', 'strategyType', 'startDate', 'endDate', 'budget']);

                if (isNewCampaign || isDraftCampaign) {
                    fields.push(
                        ...[
                            'adGroups.*.adGroupProductCategories',
                            'adGroups.*.adGroupInventorySources',
                            'budgetCapRecurrenceTimePeriod',
                            'budgetCapAmount',
                            'goal',
                            'kpi',
                            'targetKpi',
                            'targetKpiDisabled',
                            'bidStrategy',
                        ]
                    );
                }
                break;

            case STEP_CAMPAIGN_GEOGRAPHY:
                if (campaign.audienceType === 'CUSTOM') {
                    fields.push(...['adGroups.*.adGroupAudienceSources']);
                }

                fields.push(...['audienceType', 'adGroups.*.adGroupLocations', 'zipcodes']);
                break;

            case STEP_CAMPAIGN_CREATIVE:
                fields.push(...['campaignType']);

                if (campaign.campaignType === 'AdTag') {
                    fields.push(...['adTagUrl']);
                }
                break;
        }

        return fields;
    };

    const getInvalidFields = (): string[] => {
        const invalidFields = [STEP_CAMPAIGN_SETUP, STEP_CAMPAIGN_GEOGRAPHY, STEP_CAMPAIGN_CREATIVE].flatMap((step) =>
            getStepFields(step).filter((field) => isInvalidField(field))
        );

        if (!flightValidationState) {
            invalidFields.push('flights');
        }

        return Array.from(new Set(invalidFields));
    };

    const isValidStep = (step: number): boolean => {
        if (step === STEP_CAMPAIGN_SETUP && !flightValidationState) {
            return false;
        }

        const fields: string[] = getStepFields(step);

        if (fields.length > 0) {
            for (const field in fields) {
                if (isInvalidField(fields[field])) {
                    return false;
                }
            }
        }

        return true;
    };

    const stepper = useStepper({
        canNextStep: (step: number): boolean => {
            return isValidStep(step);
        },
        canPreviousStep: (step: number): boolean => {
            return !(step === 0 || saveInProgress);
        },
        defaultActiveStep: 0,
        jumpable: true,
        maxStep: 3,
        minStep: 0,
        canStep: (step: number): boolean => {
            for (let currentStep = 0; currentStep < step; currentStep++) {
                if (!isValidStep(currentStep)) {
                    return false;
                }
            }

            return !(stepper.maxStep < step || stepper.minStep > step);
        },
        onBeforeStep: async (step: number): Promise<void> => {
            if (!stepper.canStep(step)) {
                setHasFormError(true);
                stepper.setErrorStep(stepper.activeStep);
            } else {
                setHasFormError(false);
                stepper.setErrorStep(-1);

                if (!campaign.id || campaign.deliveryStatusId === 'DRAFT') {
                    try {
                        const response = await updateDraftCampaign();

                        if (!campaign.id) {
                            window.history.pushState('', '[cognition]', `/campaign/v2/${response.data.id}`);
                        }

                        setCampaign({
                            ...response.data,
                            status: null,
                            statusDate: null,
                        });
                    } catch (error) {}
                }
            }
        },
        onBeforeNextStep: async (step: number): Promise<void> => {
            if (!stepper.canNextStep()) {
                setHasFormError(true);
                stepper.setErrorStep(step);
            } else {
                setHasFormError(false);
                stepper.setErrorStep(-1);

                if (!campaign.id || campaign.deliveryStatusId === 'DRAFT') {
                    try {
                        const response = await updateDraftCampaign();

                        if (!campaign.id) {
                            window.history.pushState('', '[cognition]', `/campaign/v2/${response.data.id}`);
                        }

                        setCampaign({
                            ...response.data,
                            status: null,
                            statusDate: null,
                        });
                    } catch (error) {}
                }
            }
        },
        onBeforePreviousStep: async (step: number): Promise<void> => {
            if (!campaign.id || campaign.deliveryStatusId === 'DRAFT') {
                try {
                    const response = await updateDraftCampaign();

                    if (!campaign.id) {
                        window.history.pushState('', '[cognition]', `/campaign/v2/${response.data.id}`);
                    }

                    setCampaign({
                        ...response.data,
                        status: null,
                        statusDate: null,
                    });
                } catch (error) {}
            }
        },
    });

    const submitCampaign = (): void => {
        if (!validateCampaign()) {
            showInfoMessage('error', 'Please fill out all required fields.');
            return;
        }

        updateCampaign().then((response) => {
            setWasRecentlySubmitted(true);
        });
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        submitCampaign();
    };

    const deleteDraftCampaign = (): Promise<any> => {
        if (campaign?.campaignId) {
            return ApiService.deleteCampaign(campaign.campaignId).catch(() => {});
        }

        return Promise.resolve({});
    };

    const updateDraftCampaign = (): Promise<any> => {
        return ApiService.updateCampaign(
            toCampaignUpdatePayload({
                ...campaign,
                version: 2,
                campaignName: isNewCampaign ? getCampaignName() : campaign?.campaignName,
                deliveryStatusId: 'DRAFT',
                deliveryStatus:
                    campaign?.deliveryStatus?.dspStatusId === 'DRAFT'
                        ? campaign.deliveryStatus
                        : { dspStatusId: 'DRAFT' },
                activationStatusId: 'INACTIVE',
                startDate: campaign.startDate ? toStartOfDay(campaign.startDate) : null,
                endDate: campaign.endDate ? toEndOfDay(campaign.endDate) : null,
            }),
            files
        )
            .then((response) => {
                response.data.startDate = response.data.startDate ? Utils.toLocalTime(response.data.startDate) : null;
                response.data.endDate = response.data.endDate ? Utils.toLocalTime(response.data.endDate) : null;
                return response;
            })
            .catch(() => {});
    };

    const updateCampaign = (): Promise<any> => {
        if (userContext.isAgency()) {
            campaign.hasUpdates = true;
        }
        // Flight impression goals are the source of truth: budget mode clears them
        // (and remembers them for restore) in the delivery card, so whatever goals
        // remain on the flights here are what should be persisted. No stripping.

        if (campaign.campaignId && campaign.deliveryStatusId !== 'DRAFT') {
            setSaveInProgress(true);
            setWasRecentlyUpdated(true);

            return ApiService.updateCampaignChangeRequest(
                toCampaignUpdatePayload({
                    ...campaign,
                    version: 2,
                    startDate: campaign.startDate ? toStartOfDay(campaign.startDate) : null,
                    endDate: campaign.endDate ? toEndOfDay(campaign.endDate) : null,
                    status: campaign.status ? campaign.status : 'Pending',
                }),
                files
            );
        } else {
            const payload: CampaignUpdatePayload = toCampaignUpdatePayload({
                ...campaign,
                version: 2,
                deliveryStatusId:
                    campaign.deliveryStatusId === 'DRAFT' ? 'PENDING_COGNITION' : campaign.deliveryStatusId,
                deliveryStatus:
                    campaign.deliveryStatusId === 'DRAFT'
                        ? { dspStatusId: 'PENDING_COGNITION' }
                        : campaign.deliveryStatus,
                startDate: campaign.startDate ? toStartOfDay(campaign.startDate) : null,
                endDate: campaign.endDate ? toEndOfDay(campaign.endDate) : null,
            });

            setWasRecentlyCreated(true);

            return ApiService.updateCampaign(payload, files)
                .then(() => {
                    if (!campaign.id) {
                        // showInfoMessage('success', 'Campaign has been created.');
                    } else {
                        setFiles([]);
                    }

                    if (campaign.id && campaign.gatewayAmazonDSPIsActive !== 2) {
                        setTimeout(() => {
                            navigate(`/campaign/${campaign.id}`, {
                                state: { wasRecentlyCreated: true },
                            });
                        }, 1000);
                    }
                })
                .catch(() => {});
        }
    };

    const isValidCampaign = (): boolean => {
        if (!isValidStep(STEP_CAMPAIGN_SETUP)) {
            return false;
        }

        if (!isValidStep(STEP_CAMPAIGN_GEOGRAPHY)) {
            return false;
        }

        return isValidStep(STEP_CAMPAIGN_CREATIVE);
    };

    const validateCampaign = (): boolean => {
        setHasFormError(false);

        if (!isValidCampaign()) {
            // Surfaced for QA/support: the snackbar only says "fill out all required
            // fields", which makes reports impossible to act on without knowing the field.
            console.warn('[CampaignWizard] Submission blocked by invalid fields:', getInvalidFields());

            setHasFormError(true);
            return false;
        }
        return true;
    };

    const populateImmediateDates = (days: number = 1) => {
        let endDate = Utils.getMonthEnd(0);
        if (campaign.endDate && campaign.endDate > endDate) {
            endDate = campaign.endDate;
        }
        let startDate = new Date();
        startDate.setDate(startDate.getDate() + days);
        setCampaign((prev) => ({
            ...prev,
            startDate: startDate,
            endDate: endDate,
        }));
    };

    const fetchCampaign = useCallback(() => {
        if (campaignId && campaignChangeRequestId) {
            ApiService.getCampaignChangeRequest(campaignId, campaignChangeRequestId)
                .then((response) => {
                    let campaignChangeRequest = response.data;
                    campaignChangeRequest.changeRequestId = campaign.id;
                    campaignChangeRequest.startDate = response.data.startDate
                        ? Utils.toLocalTime(response.data.startDate)
                        : null;
                    campaignChangeRequest.endDate = response.data.endDate
                        ? Utils.toLocalTime(response.data.endDate)
                        : null;
                    setCampaign(campaignChangeRequest);
                    setIsLoaded(true);

                    if (campaignChangeRequest.status === 'Pending') {
                        populateImmediateDates(0);
                    }

                    const isComplete = campaignChangeRequest.status === 'Complete';
                    setIsCompletedCampaign(isComplete);
                    stepper.setActiveStep(isComplete ? STEP_CAMPAIGN_REVIEW : STEP_CAMPAIGN_SETUP);
                })
                .catch(() => {});
        } else if (campaignId) {
            ApiService.getCampaign(campaignId)
                .then((response) => {
                    setCampaign({
                        ...response.data,
                        startDate: response.data.startDate ? Utils.toLocalTime(response.data.startDate) : null,
                        endDate: response.data.endDate ? Utils.toLocalTime(response.data.endDate) : null,
                        status: null,
                        statusDate: null,
                    });
                    setIsLoaded(true);
                    stepper.setActiveStep(STEP_CAMPAIGN_SETUP);
                })
                .catch(() => {});
        } else {
            setIsLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, campaignChangeRequestId]);

    return {
        // Audience Sources
        $audienceSources,
        // Inventory Sources
        $inventorySources,
        // Geo Locations
        $locations,
        // Product Categories
        $productCategories,

        //
        $loader,
        $campaignGeographyTargeting,
        $campaignZipCodes,
        $campaignDefaultSettings,
        $campaignDuplicateValidator,

        campaign,
        campaignBidStrategyLabel,
        campaignBudgetManagementStrategyLabel,
        campaignId,
        campaignChangeRequestId,
        dealer,
        deleteDraftCampaign,
        fetchCampaign,
        files,
        flightValidationState,
        generateCampaignName,
        getCampaignName,
        handleSubmit,
        submitCampaign,
        hasError,
        hasFormError,
        ignoreDuplicates,
        isCompletedCampaign,
        isDraftCampaign,
        isInvalidField,
        isLoaded,
        isNewCampaign,
        isValidCampaign,
        isValidField,
        isValidStep,
        resetCampaignWizard,
        saveInProgress,
        setCampaign,
        setFiles,
        setFlightValidation,
        setHasFormError,
        setIgnoreDuplicates,
        setIsCompletedCampaign,
        setSaveInProgress,
        setWasRecentlyCreated,
        setWasRecentlyUpdated,
        setWasRecentlyLoaded,
        setWasRecentlySubmitted,
        stepper,
        updateCampaign,
        updateDraftCampaign,
        validateCampaign,
        wasRecentlyCreated,
        wasRecentlyUpdated,
        wasRecentlyLoaded,
        wasRecentlySubmitted,
    };
}
